// src/components/suiviEnseignants/AppelsEnseignant.jsx
// ============================================================================
// Page d'un enseignant — onglet « Appels »
// ============================================================================
//
// La semaine de l'enseignant, du lundi au samedi : chaque cours de son emploi
// du temps avec l'état de son appel, et, pour un titulaire, l'appel du matin
// des jours où sa classe n'a pas de cours. L'état (« fait », « sans appel »,
// « à venir », « annulé ») est décidé par le serveur avec SA date du jour.
//
// Les lignes de la grille sont les plages horaires RÉELLES des cours de la
// semaine (comme l'emploi du temps de l'espace enseignant), pas une grille
// d'heures fixe : un établissement aux cours de 55 minutes n'y perd rien.
// ============================================================================

import React from 'react';
import { CalendarOff } from 'lucide-react';
import EtatVide from '../ui/EtatVide';
import { jourCourt, libelleDe, nombre, pluriel } from './formatSuivi';

const ETATS = {
  fait: ['Appel fait', 'border-emerald-200 bg-emerald-50 text-emerald-700'],
  sans_appel: ['Sans appel', 'border-amber-400 bg-amber-50 text-amber-800'],
  a_venir: ['À venir', 'border-gray-200 bg-gray-50 text-gray-600'],
  annule: ['Annulé', 'border-gray-200 bg-white text-gray-400']
};

const heure = (valeur) => String(valeur || '').slice(0, 5);

/** « 2 abs. · 1 ret. », « Tous présents » ; rien sans totaux. */
function resumeTotaux(totaux) {
  if (!totaux) return '';
  const absents = nombre(totaux.absents);
  const retards = nombre(totaux.retards);
  if (!absents && !retards) return 'Tous présents';
  return [absents ? `${absents} abs.` : null, retards ? `${retards} ret.` : null].filter(Boolean).join(' · ');
}

/**
 * Une case de la grille. `contexte` (« Lun. 16, 07:30 – 09:30 ») n'est lu que
 * par le lecteur d'écran : la grille n'a pas de structure de tableau, et la
 * case seule ne disait ni le jour ni l'heure du cours.
 */
const Case = ({ classe, matiere, etat, totaux, contexte }) => {
  const [libelle, teinte] = ETATS[etat] || ETATS.a_venir;
  const resume = etat === 'fait' ? resumeTotaux(totaux) : '';
  return (
    // `relative` : le texte `sr-only` (en position absolue) reste dans la case
    // au lieu de se placer par rapport à la page et de l'élargir.
    <div className={`relative min-h-[52px] rounded-md border px-2 py-1.5 ${teinte}`}>
      {contexte && <span className="sr-only">{contexte} : </span>}
      <p className={`text-xs font-semibold ${etat === 'annule' ? 'line-through' : ''}`}>{libelleDe(classe) || 'Classe'}</p>
      {matiere && <p className="truncate text-[11px] opacity-80">{libelleDe(matiere)}</p>}
      <p className="text-[11px]">{libelle}{resume ? ` · ${resume}` : ''}</p>
    </div>
  );
};

const AppelsEnseignant = ({ appels, parentsOuverts = false }) => {
  const jours = Array.isArray(appels?.jours) ? appels.jours : [];
  const creneaux = Array.isArray(appels?.creneaux) ? appels.creneaux : [];
  const journees = Array.isArray(appels?.journees) ? appels.journees : [];

  if (creneaux.length === 0 && journees.length === 0) {
    return (
      <EtatVide
        compact
        icone={CalendarOff}
        titre="Aucun cours cette semaine"
        description="Ni cours a l'emploi du temps, ni appel du matin pour cet enseignant sur la semaine choisie."
      />
    );
  }

  // Les plages horaires de la semaine, dans l'ordre de la journée.
  const plages = [...new Map(
    creneaux.map((c) => [`${heure(c.heure_debut)}-${heure(c.heure_fin)}`, { debut: heure(c.heure_debut), fin: heure(c.heure_fin) }])
  ).values()].sort((a, b) => (a.debut === b.debut ? a.fin.localeCompare(b.fin) : a.debut.localeCompare(b.debut)));

  const colonnes = `120px repeat(${Math.max(jours.length, 1)}, minmax(0, 1fr))`;
  const manquants = [...creneaux, ...journees].filter((c) => c.etat === 'sans_appel').length;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <div className="min-w-[760px] space-y-1">
          <div className="grid gap-1 text-xs text-gray-500" style={{ gridTemplateColumns: colonnes }}>
            <span />
            {jours.map((j) => (
              <span key={j.date} className="text-center">{jourCourt(j.date) || j.nom}</span>
            ))}
          </div>

          {journees.length > 0 && (
            <div className="grid gap-1" style={{ gridTemplateColumns: colonnes }}>
              <span className="pt-2.5 text-xs text-gray-600">Appel du matin</span>
              {jours.map((j) => {
                const du = journees.filter((x) => x.date === j.date);
                return (
                  <div key={j.date} className="space-y-1">
                    {du.length === 0
                      ? <div className="min-h-[52px] rounded-md border border-gray-100 bg-white" />
                      : du.map((x, i) => (
                        <Case
                          key={`${x.classe}-${i}`}
                          classe={x.classe}
                          etat={x.etat}
                          totaux={x.totaux}
                          contexte={`${jourCourt(j.date) || j.nom}, appel du matin`}
                        />
                      ))}
                  </div>
                );
              })}
            </div>
          )}

          {plages.map((p) => (
            <div key={`${p.debut}-${p.fin}`} className="grid gap-1" style={{ gridTemplateColumns: colonnes }}>
              <span className="pt-2.5 text-xs text-gray-600">{p.debut} - {p.fin}</span>
              {jours.map((j) => {
                const du = creneaux.filter((c) => c.date === j.date && heure(c.heure_debut) === p.debut && heure(c.heure_fin) === p.fin);
                return (
                  <div key={j.date} className="space-y-1">
                    {du.length === 0
                      ? <div className="min-h-[52px] rounded-md border border-gray-100 bg-white" />
                      : du.map((c, i) => (
                        <Case
                          key={`${c.classe}-${i}`}
                          classe={c.classe}
                          matiere={c.matiere}
                          etat={c.etat}
                          totaux={c.totaux}
                          contexte={`${jourCourt(j.date) || j.nom}, ${p.debut} - ${p.fin}`}
                        />
                      ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs leading-relaxed text-gray-600">
        {manquants > 0 ? `${pluriel(manquants, 'cours passé', 'cours passés')} sans appel cette semaine, en orange. ` : ''}
        Les absences et retards relevés à l'appel partent à la vie scolaire
        {parentsOuverts ? " et dans l'espace des familles" : ''}. Estimation :
        l'école n'a pas de calendrier des jours sans classe, un cours prévu un jour férié apparaît « sans appel ».
      </p>
    </div>
  );
};

export default AppelsEnseignant;
